import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    IoAdd,
    IoChevronDown,
    IoCopyOutline,
    IoCreateOutline,
    IoOpenOutline,
    IoSearchOutline
} from 'react-icons/io5';
import Button from '../ui/Button';
import ShareScheduleModal from '../ShareScheduleModal';
import {
    getDrafts,
    getInstitutionSettings,
    listScheduleShares,
    updateScheduleShare
} from '../../services/apiService';
import {
    formatDraftShareOptionLabel,
    isHiddenFromVersions
} from '../../utils/scheduleDraftKind';

const formatWeekLabel = (year, week) => {
    if (year == null || week == null) return '—';
    return `${year} · Week ${week}`;
};

const formatShareRange = (share) => {
    if (!share) return '—';
    if (share.startWeekYear === share.endWeekYear && share.startWeek === share.endWeek) {
        return formatWeekLabel(share.startWeekYear, share.startWeek);
    }
    return `${formatWeekLabel(share.startWeekYear, share.startWeek)} → ${formatWeekLabel(share.endWeekYear, share.endWeek)}`;
};

const getDraftPeriodId = (draft) => {
    const raw = draft?.period_id ?? draft?.periodId;
    if (raw === undefined || raw === null || raw === '') return null;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
};

const getDraftId = (draft) => {
    const raw = draft?.id;
    if (raw === undefined || raw === null || raw === '') return null;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
};

const formatRelativeDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';

    const diffMs = Date.now() - date.getTime();
    const diffMinutes = Math.round(diffMs / 60000);
    if (diffMinutes < 1) return 'just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;

    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.round(diffHours / 24);
    if (diffDays < 14) return `${diffDays}d ago`;

    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

const formatAbsoluteDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const isShareExpired = (share) => {
    if (!share?.expiresAt) return false;
    const date = new Date(share.expiresAt);
    if (Number.isNaN(date.getTime())) return false;
    return date.getTime() < Date.now();
};

const getShareStatus = (share) => {
    if (!share?.isActive) return 'disabled';
    if (isShareExpired(share)) return 'expired';
    return 'active';
};

const buildPublicUrl = (endpoint, slug) => {
    if (!endpoint || !slug) return '';
    return `${window.location.origin}/${endpoint}/s/${slug}`;
};

const ShareCard = ({
    share,
    status,
    isHighlighted,
    isBusy,
    publicUrl,
    periods,
    copiedShareId,
    highlightRef,
    onCopy,
    onEdit,
    onDisable,
    onEnable
}) => (
    <article
        ref={isHighlighted ? highlightRef : null}
        className={`flex flex-col rounded-xl border bg-white p-4 shadow-sm transition-colors ${isHighlighted
                ? 'border-purple-400 ring-2 ring-purple-200'
                : status === 'active'
                    ? 'border-green-300'
                    : 'border-gray-200'
            }`}
    >
        <div className="mb-3 flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
                <h3 className="truncate text-base font-semibold text-gray-900" title={formatShareRange(share)}>
                    {formatShareRange(share)}
                </h3>
                <p className="mt-1 truncate font-mono text-xs text-gray-500" title={publicUrl || share.slug}>
                    {publicUrl || share.slug || 'No public URL'}
                </p>
            </div>
            <span
                className={`flex-none rounded-full px-2 py-0.5 text-[11px] font-semibold ${status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : status === 'expired'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-gray-100 text-gray-600'
                    }`}
            >
                {status === 'active' ? 'Active' : status === 'expired' ? 'Expired' : 'Disabled'}
            </span>
        </div>

        <div className="mb-4">
            <div className="mb-1.5 text-xs uppercase tracking-wide text-gray-400">Included periods</div>
            {periods.length === 0 ? (
                <div className="text-sm text-gray-500">No periods included.</div>
            ) : (
                <ul className="space-y-1.5">
                    {periods.map((period) => (
                        <li key={period.periodId} className="flex items-start gap-2 rounded-lg bg-gray-50 px-2.5 py-2">
                            <span
                                className="mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                                style={{ backgroundColor: period.color }}
                            />
                            <div className="min-w-0">
                                <div className="truncate text-sm font-medium text-gray-900">{period.name}</div>
                                <div className="truncate text-xs text-gray-500">
                                    {period.weekLabel ? `${period.weekLabel} · ` : ''}
                                    {period.draftLabel}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>

        <dl className="mb-4 text-sm">
            <dt className="text-xs uppercase tracking-wide text-gray-400">Last updated</dt>
            <dd className="mt-0.5 font-medium text-gray-800" title={formatAbsoluteDate(share.updatedAt || share.createdAt)}>
                {formatRelativeDate(share.updatedAt || share.createdAt)}
                <span className="ml-2 text-xs font-normal text-gray-400">
                    {formatAbsoluteDate(share.updatedAt || share.createdAt)}
                </span>
            </dd>
        </dl>

        <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
            <button
                type="button"
                onClick={() => onCopy(share)}
                disabled={isBusy || !publicUrl}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
                <IoCopyOutline className="h-4 w-4" />
                {copiedShareId === share.id ? 'Copied' : 'Copy link'}
            </button>
            <button
                type="button"
                onClick={() => onEdit(share)}
                disabled={isBusy}
                className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-2.5 py-2 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                title="Edit week range and versions"
                aria-label="Edit shared link"
            >
                <IoCreateOutline className="h-4 w-4" />
            </button>
            <a
                href={status === 'active' ? publicUrl : undefined}
                target="_blank"
                rel="noreferrer"
                className={`inline-flex items-center justify-center rounded-lg border border-gray-300 px-2.5 py-2 text-gray-700 hover:bg-gray-50 ${status !== 'active' || !publicUrl ? 'pointer-events-none opacity-50' : ''
                    }`}
                title="Open shared schedule"
                aria-label="Open shared schedule"
            >
                <IoOpenOutline className="h-4 w-4" />
            </a>
            {share.isActive ? (
                <button
                    type="button"
                    onClick={() => onDisable(share)}
                    disabled={isBusy}
                    className="inline-flex items-center justify-center rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Disable
                </button>
            ) : (
                <button
                    type="button"
                    onClick={() => onEnable(share)}
                    disabled={isBusy}
                    className="inline-flex items-center justify-center rounded-lg border border-green-200 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Enable
                </button>
            )}
        </div>
    </article>
);

const SharedVersionsView = ({
    timePeriods = [],
    yearStructure = null,
    shareWeekRange = null,
    currentDraftId = null,
    currentDraftPeriodId = null,
    currentDraftName = null,
    highlightSlug = null,
    setError,
    setSuccess
}) => {
    const [shares, setShares] = useState([]);
    const [drafts, setDrafts] = useState([]);
    const [endpoint, setEndpoint] = useState('');
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [busyShareId, setBusyShareId] = useState(null);
    const [copiedShareId, setCopiedShareId] = useState(null);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [editingShare, setEditingShare] = useState(null);
    const [disabledOpen, setDisabledOpen] = useState(false);
    const highlightRef = useRef(null);

    const loadShares = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const list = await listScheduleShares();
            setShares(Array.isArray(list) ? list : []);
        } catch (err) {
            if (setError) setError('Failed to load shared versions: ' + (err.message || 'Unknown error'));
        } finally {
            if (!silent) setLoading(false);
        }
    }, [setError]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                const [shareList, draftList, settings] = await Promise.all([
                    listScheduleShares(),
                    getDrafts().catch(() => []),
                    getInstitutionSettings().catch(() => null)
                ]);
                if (!mounted) return;
                setShares(Array.isArray(shareList) ? shareList : []);
                setDrafts(Array.isArray(draftList) ? draftList : []);
                if (settings?.url_endpoint) setEndpoint(settings.url_endpoint);
            } catch (err) {
                if (mounted && setError) {
                    setError('Failed to load shared versions: ' + (err.message || 'Unknown error'));
                }
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [setError]);

    const draftsByPeriodId = useMemo(() => {
        const map = new Map();
        drafts.forEach((draft) => {
            if (isHiddenFromVersions(draft)) return;
            const periodId = getDraftPeriodId(draft);
            if (periodId == null) return;
            if (!map.has(periodId)) map.set(periodId, []);
            map.get(periodId).push(draft);
        });
        return map;
    }, [drafts]);

    const getPeriodSummaries = useCallback((share) => {
        return (share?.periods || [])
            .filter((row) => row.enabled)
            .map((row) => {
                const periodId = Number(row.periodId);
                const period = row.period;
                const periodDrafts = draftsByPeriodId.get(periodId) || [];
                const pinnedId = row.scheduleDraftId == null ? null : Number(row.scheduleDraftId);
                const activeDraftId = period?.activeScheduleDraftId ?? period?.active_schedule_draft_id ?? null;
                const embeddedDraft = row.scheduleDraft
                    ? {
                        id: row.scheduleDraft.id,
                        draft_name: row.scheduleDraft.draftName,
                        period_id: row.scheduleDraft.periodId
                    }
                    : null;
                const draft = pinnedId != null
                    ? periodDrafts.find((item) => getDraftId(item) === pinnedId) || embeddedDraft
                    : periodDrafts.find((item) => getDraftId(item) === Number(activeDraftId));
                const draftLabel = pinnedId == null
                    ? (draft ? `Live — ${formatDraftShareOptionLabel(draft)}` : 'Activated (live) draft')
                    : (draft ? formatDraftShareOptionLabel(draft) : `Draft #${pinnedId}`);

                return {
                    periodId,
                    name: period?.name || `Period ${periodId}`,
                    color: period?.color || '#3B82F6',
                    weekLabel: period
                        ? `W${period.startWeek ?? period.start_week}–W${period.endWeek ?? period.end_week}`
                        : null,
                    draftLabel
                };
            });
    }, [draftsByPeriodId]);

    const matchesQuery = useCallback((share) => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return true;
        const periods = getPeriodSummaries(share);
        const haystack = [
            formatShareRange(share),
            share.slug,
            ...periods.flatMap((period) => [period.name, period.draftLabel, period.weekLabel])
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(query);
    }, [searchQuery, getPeriodSummaries]);

    const activeShares = useMemo(
        () => (shares || []).filter((share) => getShareStatus(share) === 'active').filter(matchesQuery),
        [shares, matchesQuery]
    );

    const inactiveShares = useMemo(
        () => (shares || []).filter((share) => getShareStatus(share) !== 'active').filter(matchesQuery),
        [shares, matchesQuery]
    );

    const inactiveTotal = useMemo(
        () => (shares || []).filter((share) => getShareStatus(share) !== 'active').length,
        [shares]
    );

    useEffect(() => {
        if (!highlightSlug || loading) return;
        highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [highlightSlug, loading, shares, disabledOpen]);

    useEffect(() => {
        if (highlightSlug && inactiveShares.some((share) => share.slug === highlightSlug)) {
            setDisabledOpen(true);
        }
    }, [highlightSlug, inactiveShares]);

    useEffect(() => {
        if (searchQuery.trim() && inactiveShares.length > 0) {
            setDisabledOpen(true);
        }
    }, [searchQuery, inactiveShares.length]);

    const handleCopy = async (share) => {
        const url = buildPublicUrl(endpoint, share.slug);
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            setCopiedShareId(share.id);
            window.setTimeout(() => setCopiedShareId((current) => (current === share.id ? null : current)), 2000);
        } catch (_) { /* ignore */ }
    };

    const handleDisable = async (share) => {
        if (!share?.id) return;
        const confirmed = window.confirm(
            `Disable the shared link for ${formatShareRange(share)}? Anyone with the URL will lose access.`
        );
        if (!confirmed) return;

        setBusyShareId(share.id);
        try {
            const updated = await updateScheduleShare(share.id, { revoke: true });
            setShares((prev) => prev.map((item) => (item.id === share.id ? updated : item)));
            setDisabledOpen(true);
            if (setSuccess) {
                setSuccess('Shared link disabled.');
                setTimeout(() => setSuccess(null), 3000);
            }
        } catch (err) {
            if (setError) setError('Failed to disable link: ' + (err.message || 'Unknown error'));
        } finally {
            setBusyShareId(null);
        }
    };

    const handleEnable = async (share) => {
        if (!share?.id) return;
        setBusyShareId(share.id);
        try {
            const updated = await updateScheduleShare(share.id, { isActive: true });
            setShares((prev) => prev.map((item) => (item.id === share.id ? updated : item)));
            if (setSuccess) {
                setSuccess('Shared link enabled.');
                setTimeout(() => setSuccess(null), 3000);
            }
        } catch (err) {
            if (setError) setError('Failed to enable link: ' + (err.message || 'Unknown error'));
        } finally {
            setBusyShareId(null);
        }
    };

    const handleCloseModal = () => {
        setIsCreateOpen(false);
        setEditingShare(null);
        loadShares({ silent: true });
    };

    const handleShareChange = (updated) => {
        if (editingShare && updated) {
            setShares((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
            if (setSuccess) {
                setSuccess('Shared link updated.');
                setTimeout(() => setSuccess(null), 3000);
            }
            return;
        }
        loadShares({ silent: true });
    };

    const modalShare = editingShare;
    const isModalOpen = isCreateOpen || !!editingShare;

    const renderShareCard = (share) => (
        <ShareCard
            key={share.id || share.slug}
            share={share}
            status={getShareStatus(share)}
            isHighlighted={!!(highlightSlug && share.slug === highlightSlug)}
            isBusy={busyShareId === share.id}
            publicUrl={buildPublicUrl(endpoint, share.slug)}
            periods={getPeriodSummaries(share)}
            copiedShareId={copiedShareId}
            highlightRef={highlightRef}
            onCopy={handleCopy}
            onEdit={setEditingShare}
            onDisable={handleDisable}
            onEnable={handleEnable}
        />
    );

    return (
        <div className="mx-auto max-w-6xl px-4">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900">Shared Versions</h2>
                    <p className="mt-1 text-sm text-gray-500">
                        Active public links. Disable a link to stop access; you can edit or turn it back on later.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <Button onClick={() => { setEditingShare(null); setIsCreateOpen(true); }} variant="primary" size="sm" className="inline-flex items-center gap-1.5">
                        <IoAdd className="h-4 w-4" />
                        New shared link
                    </Button>
                </div>
            </div>

            <div className="mb-5">
                <div className="relative">
                    <IoSearchOutline className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                        type="search"
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder="Search by week range, period, version, or slug…"
                        className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
            </div>

            {loading && shares.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-16 text-center text-sm text-gray-500">
                    Loading shared versions…
                </div>
            ) : (
                <>
                    {activeShares.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-16 text-center text-sm text-gray-500">
                            {shares.length === 0
                                ? 'No shared versions yet. Create a public link from the calendar or with New shared link.'
                                : searchQuery.trim()
                                    ? 'No active shared versions match this search.'
                                    : 'No active shared versions. Disabled links are below.'}
                        </div>
                    ) : (
                        <div className="grid gap-4 lg:grid-cols-2">
                            {activeShares.map(renderShareCard)}
                        </div>
                    )}

                    {inactiveTotal > 0 && (
                        <div className="mt-6">
                            <button
                                type="button"
                                onClick={() => setDisabledOpen((open) => !open)}
                                className="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 text-left text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                                aria-expanded={disabledOpen}
                            >
                                <span>
                                    Disabled links
                                    <span className="ml-2 font-normal text-gray-500">
                                        {searchQuery.trim()
                                            ? `${inactiveShares.length} of ${inactiveTotal}`
                                            : inactiveTotal}
                                    </span>
                                </span>
                                <IoChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${disabledOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {disabledOpen && (
                                <div className="mt-3">
                                    {inactiveShares.length === 0 ? (
                                        <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-8 text-center text-sm text-gray-500">
                                            No disabled links match this search.
                                        </div>
                                    ) : (
                                        <div className="grid gap-4 lg:grid-cols-2">
                                            {inactiveShares.map(renderShareCard)}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            <ShareScheduleModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                institutionUrlEndpoint={endpoint}
                timePeriods={timePeriods}
                yearStructure={yearStructure}
                initialStartWeek={modalShare?.startWeek ?? shareWeekRange?.startWeek}
                initialStartWeekYear={modalShare?.startWeekYear ?? shareWeekRange?.startWeekYear}
                initialEndWeek={modalShare?.endWeek ?? shareWeekRange?.endWeek}
                initialEndWeekYear={modalShare?.endWeekYear ?? shareWeekRange?.endWeekYear}
                currentDraftId={currentDraftId}
                currentDraftPeriodId={currentDraftPeriodId}
                currentDraftName={currentDraftName}
                existingShare={modalShare}
                onShareChange={handleShareChange}
            />
        </div>
    );
};

export default SharedVersionsView;
